import React from 'react';
import {
  Paper,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Group as GroupIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
  Business as EnterpriseIcon,
  Engineering as EngineeringIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { SignupData } from '../../pages/Auth/SignupPage';

interface SignupConfirmationStepProps {
  data: SignupData;
  onBack: () => void;
  onComplete: () => void;
  loading: boolean;
}

export const SignupConfirmationStep: React.FC<SignupConfirmationStepProps> = ({
  data,
  onBack,
  onComplete,
  loading,
}) => {
  const getPlanIcon = () => {
    switch (data.subscriptionTier) {
      case 'starter':
        return <StarIcon style={{ fontSize: 18 }} />;
      case 'professional':
        return <RocketIcon style={{ fontSize: 18 }} />;
      case 'enterprise':
        return <EnterpriseIcon style={{ fontSize: 18 }} />;
      case 'industry_specific':
        return <EngineeringIcon style={{ fontSize: 18 }} />;
      default:
        return <StarIcon style={{ fontSize: 18 }} />;
    }
  };

  const getPlanPrice = () => {
    const prices = {
      starter: { monthly: 29, annual: 290 },
      professional: { monthly: 59, annual: 590 },
      enterprise: { monthly: 89, annual: 890 },
      industry_specific: { monthly: 129, annual: 1290 },
    };

    const planPrices = prices[data.subscriptionTier];
    const price = planPrices[data.billingFrequency];

    if (data.billingFrequency === 'annual') {
      return `$${price}/year ($${Math.round(price / 12)}/month)`;
    }
    return `$${price}/month`;
  };

  const capitalize = (str: string) => {
    if (str === 'industry_specific') {
      return 'MEP Toolkit';
    }
    return str.charAt(0).toUpperCase() + str.slice(1).replace('_', ' ');
  };

  return (
    <Paper elevation={3} style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" style={{ marginBottom: 8, fontSize: '1.1rem' }}>
        Review Your Information
      </Typography>

      <Alert severity="success" style={{ marginBottom: 8, paddingTop: 6, paddingBottom: 6 }}>
        <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
          Almost done! Review your information and click "Complete Setup".
        </Typography>
      </Alert>

      <Grid container spacing={1} style={{ flex: 1, overflowY: 'auto' }}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" color="primary" style={{ marginBottom: 4, fontSize: '0.95rem', fontWeight: 500 }}>
            Account Information
          </Typography>
          <List dense style={{ paddingTop: 0, paddingBottom: 0 }}>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <PersonIcon color="action" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Name"
                secondary={`${data.firstName} ${data.lastName}`}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <EmailIcon color="action" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Email"
                secondary={data.email}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" color="primary" style={{ marginBottom: 4, fontSize: '0.95rem', fontWeight: 500 }}>
            Organization Details
          </Typography>
          <List dense style={{ paddingTop: 0, paddingBottom: 0 }}>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <BusinessIcon color="action" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Organization"
                secondary={data.organizationName}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CategoryIcon color="action" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Type & Industry"
                secondary={`${capitalize(data.organizationType)} - ${data.industry}`}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <GroupIcon color="action" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Team Size"
                secondary={`${data.teamSize} employees`}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItem>
          </List>
        </Grid>
      </Grid>

      <Divider style={{ marginTop: 8, marginBottom: 8 }} />

      <div style={{ marginBottom: 12 }}>
        <Typography variant="subtitle1" color="primary" style={{ marginBottom: 4, fontSize: '0.95rem', fontWeight: 500 }}>
          Subscription Plan
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: '#325BA8' }}>
            {getPlanIcon()}
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body1" style={{ fontSize: '0.9rem' }}>
              {capitalize(data.subscriptionTier)} Plan
              {data.billingFrequency === 'annual' && (
                <Chip
                  label="SAVE 17%"
                  color="success"
                  size="small"
                  style={{ marginLeft: 4, height: 18, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ fontSize: '0.85rem' }}>
              {getPlanPrice()}
            </Typography>
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 12 }}>
        <Typography variant="body2" style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: 4 }}>
          What happens next:
        </Typography>
        <List dense style={{ paddingTop: 0, paddingBottom: 0 }}>
          <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
            <ListItemIcon style={{ minWidth: 24 }}>
              <CheckIcon color="success" style={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Your account will be created with secure authentication"
              primaryTypographyProps={{ fontSize: '0.8rem', lineHeight: 1.4 }}
            />
          </ListItem>
          <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
            <ListItemIcon style={{ minWidth: 24 }}>
              <CheckIcon color="success" style={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Your organization's workspace will be initialized"
              primaryTypographyProps={{ fontSize: '0.8rem', lineHeight: 1.4 }}
            />
          </ListItem>
          <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
            <ListItemIcon style={{ minWidth: 24 }}>
              <CheckIcon color="success" style={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="Your knowledge graph and AI teammates will be configured"
              primaryTypographyProps={{ fontSize: '0.8rem', lineHeight: 1.4 }}
            />
          </ListItem>
          <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
            <ListItemIcon style={{ minWidth: 24 }}>
              <CheckIcon color="success" style={{ fontSize: 16 }} />
            </ListItemIcon>
            <ListItemText
              primary="You'll be redirected to your personalized dashboard"
              primaryTypographyProps={{ fontSize: '0.8rem', lineHeight: 1.4 }}
            />
          </ListItem>
        </List>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <Button onClick={onBack} size="medium" disabled={loading}>
          Back
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={onComplete}
          disabled={loading}
          style={{ paddingLeft: 24, paddingRight: 24 }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} color="inherit" style={{ marginRight: 4 }} />
              Setting up...
            </>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </div>

      {/* Loading Modal */}
      <Dialog
        open={loading}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: 8,
            minWidth: 400,
          }
        }}
      >
        <DialogContent style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32, paddingLeft: 24, paddingRight: 24 }}>
          <CircularProgress size={48} style={{ marginBottom: 24 }} />
          <Typography variant="h6" style={{ marginBottom: 16, fontSize: '1.1rem' }}>
            Setting Up Your Organization
          </Typography>
          <Typography variant="body2" style={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
            Please wait while we complete your setup:
          </Typography>
          <List dense style={{ textAlign: 'left', maxWidth: 300, margin: '0 auto' }}>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CheckIcon color="success" style={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary="Creating your secure account"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Initializing knowledge graph"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Configuring AI teammates"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
            <ListItem style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 0, paddingRight: 0 }}>
              <ListItemIcon style={{ minWidth: 32 }}>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Setting up team workspace"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
          </List>
          <Typography variant="caption" style={{ display: 'block', marginTop: 24, fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)' }}>
            This usually takes 10-15 seconds...
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};
